import React, { useMemo, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { computeLeaders, fishReaction } from "@/lib/helpers";
import { computeBonusAwards } from "@/lib/awards";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { ChevronRight, Download, Share2, PartyPopper } from "lucide-react";

function Confetti() {
  // 24 falling emoji sprites — pure CSS animation, no lib
  const items = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((i) => {
        const emoji = ["🎣", "🐟", "🐠", "🏆", "🎉"][i % 5];
        const left = Math.round((i * 37) % 100);
        const delay = (i % 8) * 0.15;
        const duration = 2.4 + (i % 5) * 0.35;
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              top: "-40px",
              left: `${left}%`,
              fontSize: `${18 + (i % 4) * 8}px`,
              animation: `wc-fall ${duration}s cubic-bezier(0.3, 0.5, 0.6, 1) ${delay}s forwards`,
              opacity: 0.9,
            }}
          >
            {emoji}
          </span>
        );
      })}
      <style>{`
        @keyframes wc-fall {
          0% { transform: translateY(-40px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}

const CERT_KEY_PREFIX = "glenlyon.ceremonySeen.";

function certificateKey(family) {
  return `${CERT_KEY_PREFIX}${family?.tournament_end || "current"}`;
}

export function hasSeenCeremony(family) {
  return localStorage.getItem(certificateKey(family)) === "1";
}

export function markCeremonySeen(family) {
  localStorage.setItem(certificateKey(family), "1");
}

function AwardCard({ award, family, big, showConfetti, testIdPrefix, cardRef }) {
  const year = new Date(family.tournament_end).getFullYear();
  return (
    <div
      ref={cardRef}
      className="relative overflow-hidden rounded-3xl border-2 border-[#E8A838]/50 bg-gradient-to-br from-[#0A3D62] to-[#082e4a] text-[#FAF8F3] p-7 shadow-[0_20px_60px_rgba(10,61,98,0.35)]"
      data-testid={`${testIdPrefix}-card`}
      style={{ minHeight: big ? "460px" : "380px" }}
    >
      {showConfetti && <Confetti />}
      <div className="relative flex flex-col items-center text-center gap-3">
        <div className={`${big ? "text-7xl" : "text-6xl"} drop-shadow`}>
          {award.icon}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E8A838]">
          {big ? "🏆 Champion" : "Award"}
        </div>
        <div className="font-display font-black text-2xl text-[#E8A838] leading-tight">
          {award.title}
        </div>
        <div className="mt-2 flex flex-col items-center gap-2">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-5xl border-4 border-[#E8A838]/60"
            style={{ background: (award.member?.color || "#E8A838") + "44" }}
          >
            {award.member?.emoji || "🎣"}
          </div>
          <div className="font-display font-black text-4xl leading-tight tracking-tight">
            {award.member?.name || "—"}
          </div>
          <div className="font-display text-xl text-[#E8A838] font-black">
            {award.stat}
          </div>
        </div>
        <p className="text-sm text-[#FAF8F3]/80 italic mt-1 max-w-xs">"{award.copy}"</p>
        <div className="mt-4 pt-4 border-t border-[#FAF8F3]/15 text-[10px] uppercase tracking-widest text-[#FAF8F3]/60">
          {family.comp_name} · Easter {year}
        </div>
      </div>
    </div>
  );
}

async function saveCardAsPng(el, filename) {
  const dataUrl = await toPng(el, {
    cacheBust: true,
    pixelRatio: 2,
    backgroundColor: "#0A3D62",
  });
  return dataUrl;
}

async function shareOrDownload(dataUrl, filename) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const file = new File([blob], filename, { type: "image/png" });

  if (
    navigator.canShare &&
    navigator.canShare({ files: [file] }) &&
    typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({ files: [file], title: filename });
      return { shared: true };
    } catch (_) {
      // fall through to download
    }
  }
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return { downloaded: true };
}

export default function Ceremony({ onDone }) {
  const { family, members, catches } = useApp();
  const leaders = useMemo(() => computeLeaders(catches, members), [catches, members]);
  const bonus = useMemo(() => computeBonusAwards(catches, members), [catches, members]);

  // Build ordered award list: smallest → most caught → biggest → bonuses
  const awards = useMemo(() => {
    const list = [];
    if (leaders.smallest?.member) {
      const c = catches.find(
        (x) =>
          x.catch_owner_id === leaders.smallest.member.id &&
          x.length_cm === leaders.smallest.cm,
      );
      list.push({
        key: "smallest",
        icon: "🐟",
        title: "Smallest Fish",
        member: leaders.smallest.member,
        stat: `${leaders.smallest.cm.toFixed(0)}cm`,
        copy: `The proud minnow trophy — ${c?.species ? c.species.toLowerCase() + ", " : ""}${leaders.smallest.cm.toFixed(0)}cm and every bit of it.`,
        big: false,
        confetti: false,
      });
    }
    if (leaders.mostCaught?.member) {
      list.push({
        key: "most-caught",
        icon: "🎣",
        title: "Most Caught",
        member: leaders.mostCaught.member,
        stat: `${leaders.mostCaught.count} catches`,
        copy: `Sheer volume champion. ${leaders.mostCaught.member.name} out-fished the family.`,
        big: false,
        confetti: false,
      });
    }
    if (leaders.biggest?.member) {
      const c = catches.find(
        (x) =>
          x.catch_owner_id === leaders.biggest.member.id &&
          x.length_cm === leaders.biggest.cm,
      );
      list.push({
        key: "biggest",
        icon: "🏆",
        title: "Biggest Fish",
        member: leaders.biggest.member,
        stat: `${leaders.biggest.cm.toFixed(0)}cm ${c?.species || ""}`.trim(),
        copy: `The headline. A ${leaders.biggest.cm.toFixed(0)}cm ${(c?.species || "unit").toLowerCase()} — the fish everyone will lie about at Christmas.`,
        big: true,
        confetti: true,
      });
    }
    return [...list, ...bonus.map((a) => ({ ...a, big: false, confetti: false }))];
  }, [leaders, bonus, catches]);

  const [idx, setIdx] = useState(0);
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);

  if (awards.length === 0) {
    // No trophies to hand out — skip straight to results
    onDone?.();
    return null;
  }

  const current = awards[idx];
  const isLast = idx === awards.length - 1;

  const handleNext = () => {
    if (!isLast) {
      setIdx(idx + 1);
    } else {
      markCeremonySeen(family);
      onDone?.();
    }
  };

  const handleSave = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const filename = `${(current.member?.name || "winner").replace(/\W+/g, "-")}-${current.title.replace(/\W+/g, "-")}.png`;
      const dataUrl = await saveCardAsPng(cardRef.current, filename);
      const result = await shareOrDownload(dataUrl, filename);
      toast(result.shared ? "Shared." : "Saved. Frame it.");
    } catch (e) {
      console.error(e);
      toast.error("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] wc-safe-top bg-[#082e4a] text-[#FAF8F3] flex flex-col overflow-y-auto" data-testid="ceremony-screen">
      {/* Progress */}
      <div className="px-5 pt-6 pb-2 flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-[#E8A838] font-bold flex items-center gap-2">
          <PartyPopper className="w-4 h-4" strokeWidth={2.5} />
          Final Standings
        </div>
        <div className="text-xs opacity-70 font-semibold">
          {idx + 1} / {awards.length}
        </div>
      </div>
      <div className="px-5">
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#E8A838] transition-all duration-500"
            style={{ width: `${((idx + 1) / awards.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Card */}
      <div className="flex-1 px-5 pt-6 pb-6 flex items-center">
        <div className="w-full">
          <AwardCard
            key={current.key}
            award={current}
            family={family}
            big={current.big}
            showConfetti={current.confetti}
            testIdPrefix={`ceremony-${current.key}`}
            cardRef={cardRef}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 pb-8 space-y-2">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="ceremony-save-btn"
          className="w-full h-14 rounded-2xl bg-white/10 border border-white/15 text-[#FAF8F3] font-bold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60"
        >
          <Share2 className="w-5 h-5" strokeWidth={2.5} />
          {saving ? "Rendering…" : "Save / share certificate"}
        </button>
        <button
          onClick={handleNext}
          data-testid="ceremony-next-btn"
          className="w-full h-16 rounded-2xl bg-[#E8A838] text-[#1A2A35] font-display font-black text-lg flex items-center justify-center gap-2 active:scale-[0.98]"
        >
          {isLast ? "See full results" : "Next award"}
          <ChevronRight className="w-6 h-6" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
